/**
 * Challenge API
 *
 * Mock challenge service with localStorage persistence.
 */

import type {
  Challenge,
  ChallengeCategory,
  UserChallenge,
  UserChallengeStatus,
  ChallengeFilters,
  ChallengeSortBy,
  ChallengeDiscussion,
  ApiResponse,
  PaginatedResponse,
  UUID,
} from '../types'
import { mockDb, delay } from './mockDb'
import { success, error } from './client'

/**
 * Gallery item for a challenge's Track Records gallery
 */
export interface ChallengeGalleryItem {
  id: string
  challengeId: string
  userId: string
  userDisplayName: string
  imageUrl: string
  caption: string
  createdAt: string
  updatedAt: string
}

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

/**
 * Challenge API
 */
export const challengeApi = {
  /**
   * Get all challenges with filters and pagination
   */
  async getChallenges(
    filters?: ChallengeFilters,
    sortBy: ChallengeSortBy = 'newest',
    page = 1,
    limit = 20
  ): Promise<PaginatedResponse<Challenge>> {
    await delay(100)

    let challenges = mockDb.getAll<Challenge>('challenges')

    // Filter by status (only show approved)
    challenges = challenges.filter((c) => c.status === 'approved')

    // Apply filters
    if (filters) {
      if (filters.search) {
        const search = filters.search.toLowerCase()
        challenges = challenges.filter(
          (c) =>
            c.title.toLowerCase().includes(search) ||
            c.description.toLowerCase().includes(search)
        )
      }

      if (filters.categoryIds?.length) {
        challenges = challenges.filter((c) =>
          c.categoryIds.some((id) => filters.categoryIds!.includes(id))
        )
      }

      if (filters.difficulty?.length) {
        challenges = challenges.filter((c) => filters.difficulty!.includes(c.difficulty))
      }

      if (filters.isFree !== undefined) {
        challenges = challenges.filter((c) => c.isFree === filters.isFree)
      }

      if (filters.isFeatured) {
        challenges = challenges.filter((c) => c.isFeatured)
      }

      if (filters.soloGroup?.length) {
        challenges = challenges.filter((c) => filters.soloGroup!.includes(c.soloGroup))
      }

      if (filters.vendorId) {
        challenges = challenges.filter((c) => c.vendorId === filters.vendorId)
      }
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        challenges.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        break
      case 'popular':
        challenges.sort((a, b) => b.completionCount - a.completionCount)
        break
      case 'rating':
        challenges.sort((a, b) => (b.averageRating ?? 0) - (a.averageRating ?? 0))
        break
      case 'price_low':
        challenges.sort((a, b) => (a.costAmount ?? 0) - (b.costAmount ?? 0))
        break
      case 'price_high':
        challenges.sort((a, b) => (b.costAmount ?? 0) - (a.costAmount ?? 0))
        break
    }

    // Paginate
    const total = challenges.length
    const totalPages = Math.ceil(total / limit)
    const start = (page - 1) * limit
    const paginatedChallenges = challenges.slice(start, start + limit)

    return {
      success: true,
      data: paginatedChallenges,
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasMore: page < totalPages,
      },
    }
  },

  /**
   * Get a single challenge by ID
   */
  async getChallengeById(id: UUID): Promise<ApiResponse<Challenge | null>> {
    await delay(50)

    const challenge = mockDb.findById<Challenge>('challenges', id)
    return success(challenge)
  },

  /**
   * Get featured challenges
   */
  async getFeaturedChallenges(limit = 6): Promise<ApiResponse<Challenge[]>> {
    await delay(50)

    const challenges = mockDb.getAll<Challenge>('challenges')
      .filter((c) => c.status === 'approved' && c.isFeatured)
      .slice(0, limit)

    return success(challenges)
  },

  /**
   * Get all categories
   */
  async getCategories(): Promise<ApiResponse<ChallengeCategory[]>> {
    await delay(50)

    const categories = mockDb.getAll<ChallengeCategory>('categories')
      .filter((c) => c.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder)

    return success(categories)
  },

  /**
   * Get user's challenges (My Challenges)
   */
  async getMyChallenges(
    status?: UserChallengeStatus | UserChallengeStatus[],
    page = 1,
    limit = 20
  ): Promise<PaginatedResponse<UserChallenge>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return {
        success: true,
        data: [],
        pagination: { page: 1, limit, total: 0, totalPages: 0, hasMore: false },
      }
    }

    let userChallenges = mockDb.findWhere<UserChallenge>(
      'userChallenges',
      (uc) => uc.userId === userId
    )

    // Filter by status
    if (status) {
      const statuses = Array.isArray(status) ? status : [status]
      userChallenges = userChallenges.filter((uc) => statuses.includes(uc.status))
    }

    // Populate challenge data
    userChallenges = userChallenges.map((uc) => ({
      ...uc,
      challenge: mockDb.findById<Challenge>('challenges', uc.challengeId) ?? undefined,
    }))

    // Sort by most recent activity
    userChallenges.sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )

    // Paginate
    const total = userChallenges.length
    const totalPages = Math.ceil(total / limit)
    const start = (page - 1) * limit
    const paginatedResults = userChallenges.slice(start, start + limit)

    return {
      success: true,
      data: paginatedResults,
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasMore: page < totalPages,
      },
    }
  },

  /**
   * Get challenges by tab for My Challenges page
   */
  async getMyChallengesByTab(
    tab: 'invitations' | 'from_others' | 'in_progress' | 'in_review' | 'completed' | 'unverified' | 'declined'
  ): Promise<ApiResponse<UserChallenge[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return success([])
    }

    const statusMap: Record<string, UserChallengeStatus[]> = {
      invitations: ['invited'],
      from_others: ['assigned'],
      in_progress: ['in_progress'],
      in_review: ['submitted'],
      completed: ['verified'],
      unverified: ['rejected', 'unverified'],
      declined: ['declined'],
    }

    const statuses = statusMap[tab] ?? []

    let userChallenges = mockDb.findWhere<UserChallenge>(
      'userChallenges',
      (uc) => uc.userId === userId && statuses.includes(uc.status)
    )

    // Populate challenge data
    userChallenges = userChallenges.map((uc) => ({
      ...uc,
      challenge: mockDb.findById<Challenge>('challenges', uc.challengeId) ?? undefined,
    }))

    return success(userChallenges)
  },

  /**
   * Get a map of challengeId → status for all the current user's challenges
   */
  async getMyChallengeStatusMap(): Promise<ApiResponse<Record<string, UserChallengeStatus>>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return success({})
    }

    const userChallenges = mockDb.findWhere<UserChallenge>(
      'userChallenges',
      (uc) => uc.userId === userId
    )

    const statusMap: Record<string, UserChallengeStatus> = {}
    for (const uc of userChallenges) {
      statusMap[uc.challengeId] = uc.status
    }

    return success(statusMap)
  },

  /**
   * Start a challenge
   */
  async startChallenge(challengeId: UUID): Promise<ApiResponse<UserChallenge>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Check if already started
    const existing = mockDb.findOne<UserChallenge>(
      'userChallenges',
      (uc) => uc.userId === userId && uc.challengeId === challengeId && uc.status === 'in_progress'
    )

    if (existing) {
      return error('Challenge already in progress')
    }

    // Count previous completions
    const completions = mockDb.findWhere<UserChallenge>(
      'userChallenges',
      (uc) => uc.userId === userId && uc.challengeId === challengeId && uc.status === 'verified'
    )

    const userChallenge = mockDb.insert<UserChallenge>('userChallenges', {
      userId,
      challengeId,
      status: 'in_progress',
      source: 'self',
      completionNumber: completions.length + 1,
      verificationAttempts: 0,
      maxAttempts: 3,
      startedAt: new Date().toISOString(),
    })

    // Populate challenge
    userChallenge.challenge = mockDb.findById<Challenge>('challenges', challengeId) ?? undefined

    return success(userChallenge)
  },

  /**
   * Accept a gifted challenge invitation
   */
  async acceptInvitation(userChallengeId: UUID): Promise<ApiResponse<UserChallenge>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const userChallenge = mockDb.findById<UserChallenge>('userChallenges', userChallengeId)

    if (!userChallenge) {
      return error('Invitation not found')
    }

    if (userChallenge.userId !== userId) {
      return error('Not authorized')
    }

    if (userChallenge.status !== 'invited') {
      return error('This invitation has already been responded to')
    }

    const updated = mockDb.update<UserChallenge>('userChallenges', userChallengeId, {
      status: 'in_progress',
      startedAt: new Date().toISOString(),
    })

    if (!updated) {
      return error('Failed to accept invitation')
    }

    updated.challenge = mockDb.findById<Challenge>('challenges', updated.challengeId) ?? undefined

    return success(updated)
  },

  /**
   * Decline a gifted challenge invitation
   */
  async declineInvitation(userChallengeId: UUID): Promise<ApiResponse<UserChallenge>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const userChallenge = mockDb.findById<UserChallenge>('userChallenges', userChallengeId)

    if (!userChallenge) {
      return error('Invitation not found')
    }

    if (userChallenge.userId !== userId) {
      return error('Not authorized')
    }

    if (userChallenge.status !== 'invited' && userChallenge.status !== 'assigned') {
      return error('This invitation has already been responded to')
    }

    const updated = mockDb.update<UserChallenge>('userChallenges', userChallengeId, {
      status: 'declined',
    })

    if (!updated) {
      return error('Failed to decline invitation')
    }

    updated.challenge = mockDb.findById<Challenge>('challenges', updated.challengeId) ?? undefined

    return success(updated)
  },

  /**
   * Abandon a challenge
   */
  async abandonChallenge(userChallengeId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const userChallenge = mockDb.findById<UserChallenge>('userChallenges', userChallengeId)

    if (!userChallenge) {
      return error('Challenge not found')
    }

    if (userChallenge.userId !== userId) {
      return error('Not authorized')
    }

    if (userChallenge.status !== 'in_progress') {
      return error('Can only abandon challenges in progress')
    }

    mockDb.update<UserChallenge>('userChallenges', userChallengeId, {
      status: 'abandoned',
      abandonedAt: new Date().toISOString(),
    })

    return success(undefined)
  },

  /**
   * Create a new challenge (for vendors)
   */
  async createChallenge(
    data: Omit<Challenge, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'completionCount' | 'trackRecordCount' | 'isFeatured'>
  ): Promise<ApiResponse<Challenge>> {
    await delay(200)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Create challenge with pending_review status
    const challenge = mockDb.insert<Challenge>('challenges', {
      ...data,
      creatorUserId: userId,
      status: 'draft',
      completionCount: 0,
      trackRecordCount: 0,
      isFeatured: false,
    })

    return success(challenge)
  },

  /**
   * Update an existing challenge (for vendors).
   *
   * - If the challenge status is `draft` or `rejected`, the challenge is updated directly.
   * - If the challenge status is `approved` (live), the edit is stored as a `pendingEdit`
   *   property on the challenge record. The challenge data itself remains unchanged so
   *   the live version is unaffected until the revision is reviewed and approved.
   */
  async updateChallenge(
    id: UUID,
    data: Partial<Challenge>
  ): Promise<ApiResponse<Challenge>> {
    await delay(150)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const existing = mockDb.findById<Challenge>('challenges', id)
    if (!existing) {
      return error('Challenge not found')
    }

    // Verify ownership
    if (existing.creatorUserId !== userId && existing.vendorId !== userId) {
      return error('Not authorized to edit this challenge')
    }

    // If the challenge is approved (live), store the edit as a pending revision
    if (existing.status === 'approved') {
      const pendingEdit = {
        editData: data,
        submittedAt: new Date().toISOString(),
        status: 'pending_review' as const,
      }

      const updated = mockDb.update<Challenge & { pendingEdit?: { editData: Partial<Challenge>; submittedAt: string; status: 'pending_review' } }>('challenges', id, {
        pendingEdit,
      } as Partial<Challenge>)

      if (!updated) {
        return error('Failed to save pending revision')
      }

      return success(updated as Challenge)
    }

    // Only allow direct editing of drafts or rejected challenges
    if (!['draft', 'rejected'].includes(existing.status)) {
      return error('Can only edit draft or rejected challenges')
    }

    const updated = mockDb.update<Challenge>('challenges', id, data)
    if (!updated) {
      return error('Failed to update challenge')
    }

    return success(updated)
  },

  /**
   * Submit challenge for review
   */
  async submitForReview(id: UUID): Promise<ApiResponse<Challenge>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const challenge = mockDb.findById<Challenge>('challenges', id)
    if (!challenge) {
      return error('Challenge not found')
    }

    if (challenge.creatorUserId !== userId && challenge.vendorId !== userId) {
      return error('Not authorized')
    }

    if (challenge.status !== 'draft' && challenge.status !== 'rejected') {
      return error('Can only submit draft or rejected challenges for review')
    }

    const updated = mockDb.update<Challenge>('challenges', id, {
      status: 'pending_review',
    })

    if (!updated) {
      return error('Failed to submit for review')
    }

    return success(updated)
  },

  /**
   * Get vendor's own challenges
   */
  async getVendorChallenges(): Promise<ApiResponse<Challenge[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const challenges = mockDb.findWhere<Challenge>(
      'challenges',
      (c) => c.creatorUserId === userId || c.vendorId === userId
    )

    // Sort by newest first
    challenges.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    return success(challenges)
  },

  /**
   * Get challenge stats for dashboard
   */
  async getMyChallengeStats(): Promise<ApiResponse<{
    invitations: number
    fromOthers: number
    inProgress: number
    inReview: number
    completed: number
    unverified: number
    declined: number
  }>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return success({
        invitations: 0,
        fromOthers: 0,
        inProgress: 0,
        inReview: 0,
        completed: 0,
        unverified: 0,
        declined: 0,
      })
    }

    const userChallenges = mockDb.findWhere<UserChallenge>(
      'userChallenges',
      (uc) => uc.userId === userId
    )

    return success({
      invitations: userChallenges.filter((uc) => uc.status === 'invited').length,
      fromOthers: userChallenges.filter((uc) => uc.status === 'assigned').length,
      inProgress: userChallenges.filter((uc) => uc.status === 'in_progress').length,
      inReview: userChallenges.filter((uc) => uc.status === 'submitted').length,
      completed: userChallenges.filter((uc) => uc.status === 'verified').length,
      unverified: userChallenges.filter((uc) =>
        uc.status === 'rejected' || uc.status === 'unverified'
      ).length,
      declined: userChallenges.filter((uc) => uc.status === 'declined').length,
    })
  },

  /**
   * Get user's active UserChallenge for a specific challenge (if any)
   */
  async getUserChallengeForChallenge(challengeId: UUID): Promise<ApiResponse<UserChallenge | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return success(null)
    }

    const userChallenge = mockDb.findOne<UserChallenge>(
      'userChallenges',
      (uc) =>
        uc.userId === userId &&
        uc.challengeId === challengeId &&
        !['abandoned', 'declined'].includes(uc.status)
    )

    if (userChallenge) {
      userChallenge.challenge = mockDb.findById<Challenge>('challenges', challengeId) ?? undefined
    }

    return success(userChallenge)
  },

  // ============================================================================
  // Discussion Posts
  // ============================================================================

  /**
   * Get discussion posts for a challenge
   */
  async getDiscussionPosts(challengeId: string): Promise<ApiResponse<ChallengeDiscussion[]>> {
    await delay(100)

    // Return any previously persisted posts for this challenge
    let posts = mockDb.findWhere<ChallengeDiscussion>(
      'discussionPosts',
      (p) => p.challengeId === challengeId
    )

    // Seed mock posts if none exist yet
    if (posts.length === 0) {
      const mockPosts: Omit<ChallengeDiscussion, 'id' | 'createdAt' | 'updatedAt'>[] = [
        {
          challengeId,
          authorUserId: 'user-seed-1',
          content: 'Has anyone tried this challenge with younger kids? My 10-year-old wants to join in but I want to make sure the difficulty is manageable.',
          isVendorResponse: false,
          isFlagged: false,
          author: { id: 'user-seed-1', displayName: 'Sarah M.', isVendor: false },
        },
        {
          challengeId,
          authorUserId: 'user-seed-2',
          content: 'Just finished this challenge last week! Pro tip: make sure you document each step as you go. It makes the Track Record submission so much easier.',
          isVendorResponse: false,
          isFlagged: false,
          author: { id: 'user-seed-2', displayName: 'Marcus T.', isVendor: false },
        },
        {
          challengeId,
          authorUserId: 'user-seed-3',
          parentPostId: undefined,
          content: 'Great question, Sarah! We designed this challenge to be adaptable for ages 8+. For younger participants, we recommend working alongside an adult for the more complex steps.',
          isVendorResponse: true,
          isFlagged: false,
          author: { id: 'user-seed-3', displayName: 'Challenge Team', isVendor: true },
        },
        {
          challengeId,
          authorUserId: 'user-seed-4',
          content: 'I used this as a weekend family activity and it was a huge hit. We spent about 3 hours total including clean-up. Highly recommend!',
          isVendorResponse: false,
          isFlagged: false,
          author: { id: 'user-seed-4', displayName: 'Jamie L.', isVendor: false },
        },
      ]

      for (const post of mockPosts) {
        mockDb.insert<ChallengeDiscussion>('discussionPosts', post)
      }

      // Make the vendor reply a response to the first post
      posts = mockDb.findWhere<ChallengeDiscussion>(
        'discussionPosts',
        (p) => p.challengeId === challengeId
      )

      // Set the vendor response as a reply to the first post
      const firstPost = posts.find((p) => p.authorUserId === 'user-seed-1')
      const vendorReply = posts.find((p) => p.isVendorResponse)
      if (firstPost && vendorReply) {
        mockDb.update<ChallengeDiscussion>('discussionPosts', vendorReply.id, {
          parentPostId: firstPost.id,
        })
      }

      // Re-fetch after updates
      posts = mockDb.findWhere<ChallengeDiscussion>(
        'discussionPosts',
        (p) => p.challengeId === challengeId
      )
    }

    // Sort by newest first
    posts.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())

    return success(posts)
  },

  /**
   * Create a new discussion post
   */
  async createDiscussionPost(
    challengeId: string,
    content: string,
    parentId?: string
  ): Promise<ApiResponse<ChallengeDiscussion>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Look up the user's display name
    const user = mockDb.findById<{ id: string; displayName: string }>('users', userId)
    const displayName = user?.displayName || 'Anonymous User'

    const post = mockDb.insert<ChallengeDiscussion>('discussionPosts', {
      challengeId,
      authorUserId: userId,
      parentPostId: parentId,
      content,
      isVendorResponse: false,
      isFlagged: false,
      author: { id: userId, displayName, isVendor: false },
    })

    return success(post)
  },

  /**
   * Edit a discussion post
   */
  async editDiscussionPost(postId: string, content: string): Promise<ApiResponse<ChallengeDiscussion>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const existing = mockDb.findById<ChallengeDiscussion>('discussionPosts', postId)
    if (!existing) {
      return error('Post not found')
    }

    if (existing.authorUserId !== userId) {
      return error('Not authorized to edit this post')
    }

    const updated = mockDb.update<ChallengeDiscussion>('discussionPosts', postId, {
      content,
    })

    if (!updated) {
      return error('Failed to update post')
    }

    return success(updated)
  },

  /**
   * Delete a discussion post
   */
  async deleteDiscussionPost(postId: string): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const existing = mockDb.findById<ChallengeDiscussion>('discussionPosts', postId)
    if (!existing) {
      return error('Post not found')
    }

    if (existing.authorUserId !== userId) {
      return error('Not authorized to delete this post')
    }

    mockDb.delete('discussionPosts', postId)
    return success(undefined)
  },

  /**
   * Flag a discussion post for moderation
   */
  async flagDiscussionPost(postId: string): Promise<ApiResponse<ChallengeDiscussion>> {
    await delay(100)

    const existing = mockDb.findById<ChallengeDiscussion>('discussionPosts', postId)
    if (!existing) {
      return error('Post not found')
    }

    const updated = mockDb.update<ChallengeDiscussion>('discussionPosts', postId, {
      isFlagged: true,
    })

    if (!updated) {
      return error('Failed to flag post')
    }

    return success(updated)
  },

  // ============================================================================
  // Challenge Gallery
  // ============================================================================

  /**
   * Get gallery items for a challenge (user-submitted Track Record media)
   */
  async getChallengeGallery(challengeId: string): Promise<ApiResponse<ChallengeGalleryItem[]>> {
    await delay(100)

    // Return any previously persisted items
    let items = mockDb.findWhere<ChallengeGalleryItem>(
      'challengeGalleryItems',
      (item) => item.challengeId === challengeId
    )

    // Seed mock gallery items if none exist yet
    if (items.length === 0) {
      const mockItems: Omit<ChallengeGalleryItem, 'id' | 'createdAt' | 'updatedAt'>[] = [
        {
          challengeId,
          userId: 'user-seed-1',
          userDisplayName: 'Sarah M.',
          imageUrl: `https://picsum.photos/seed/${challengeId}-1/400/300`,
          caption: 'Starting my journey! Got all the materials laid out and ready to go.',
        },
        {
          challengeId,
          userId: 'user-seed-2',
          userDisplayName: 'Marcus T.',
          imageUrl: `https://picsum.photos/seed/${challengeId}-2/400/300`,
          caption: 'Halfway through and loving every minute of it.',
        },
        {
          challengeId,
          userId: 'user-seed-4',
          userDisplayName: 'Jamie L.',
          imageUrl: `https://picsum.photos/seed/${challengeId}-3/400/300`,
          caption: 'My completed project! Took about 3 hours total.',
        },
        {
          challengeId,
          userId: 'user-seed-5',
          userDisplayName: 'Alex R.',
          imageUrl: `https://picsum.photos/seed/${challengeId}-4/400/300`,
          caption: 'Family edition - the kids had a blast with this one!',
        },
        {
          challengeId,
          userId: 'user-seed-6',
          userDisplayName: 'Priya K.',
          imageUrl: `https://picsum.photos/seed/${challengeId}-5/400/300`,
          caption: 'Second time doing this challenge. Turned out even better!',
        },
        {
          challengeId,
          userId: 'user-seed-7',
          userDisplayName: 'David W.',
          imageUrl: `https://picsum.photos/seed/${challengeId}-6/400/300`,
          caption: 'Progress shot from day two. Learning so much already.',
        },
        {
          challengeId,
          userId: 'user-seed-8',
          userDisplayName: 'Mina S.',
          imageUrl: `https://picsum.photos/seed/${challengeId}-7/400/300`,
          caption: 'Used a slightly different technique for step 3 and it worked great.',
        },
      ]

      for (const item of mockItems) {
        mockDb.insert<ChallengeGalleryItem>('challengeGalleryItems', item)
      }

      items = mockDb.findWhere<ChallengeGalleryItem>(
        'challengeGalleryItems',
        (item) => item.challengeId === challengeId
      )
    }

    // Sort by newest first
    items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    return success(items)
  },

  /**
   * Get version history for a challenge
   */
  async getVersionHistory(challengeId: UUID): Promise<ApiResponse<{
    version: string
    date: string
    author: string
    summary: string
    status: 'Published' | 'Draft' | 'Archived'
    isCurrent: boolean
    changes: { additions: string[]; removals: string[] }
  }[]>> {
    await delay(400)

    // Look up challenge to make the history contextual
    const challenge = mockDb.findById<Challenge>('challenges', challengeId)
    const title = challenge?.title ?? 'this challenge'

    return success([
      {
        version: 'v2.1',
        date: new Date(Date.now() - 7 * 86400000).toISOString(),
        author: challenge?.vendorName ?? 'Author',
        summary: `Updated age recommendations and safety guidelines for ${title}.`,
        status: 'Published' as const,
        isCurrent: true,
        changes: {
          additions: [
            '+ Updated recommended age range based on feedback',
            '+ Added safety guidelines section',
            '+ Added parent supervision note',
          ],
          removals: [
            '- Removed outdated age range',
            '- Removed generic safety disclaimer',
          ],
        },
      },
      {
        version: 'v2.0',
        date: new Date(Date.now() - 30 * 86400000).toISOString(),
        author: challenge?.vendorName ?? 'Author',
        summary: 'Major revision: restructured steps and added photo evidence requirements.',
        status: 'Archived' as const,
        isCurrent: false,
        changes: {
          additions: [
            '+ Restructured challenge into clear steps',
            '+ Added photo evidence requirement',
            '+ Added estimated time',
          ],
          removals: [
            '- Removed single-paragraph instructions',
            '- Removed optional completion criteria',
          ],
        },
      },
      {
        version: 'v1.0',
        date: new Date(Date.now() - 90 * 86400000).toISOString(),
        author: challenge?.vendorName ?? 'Author',
        summary: 'Initial challenge creation.',
        status: 'Archived' as const,
        isCurrent: false,
        changes: {
          additions: [
            '+ Created challenge title and description',
            '+ Added instructions and materials list',
            '+ Set difficulty level',
          ],
          removals: [],
        },
      },
    ])
  },

  /**
   * Restore a previous version of a challenge (creates new draft)
   */
  async restoreVersion(_challengeId: UUID, _version: string): Promise<ApiResponse<boolean>> {
    await delay(600)
    return success(true)
  },

  // ============================================================================
  // Partner Finding
  // ============================================================================

  /**
   * Get users seeking partners for a specific challenge
   */
  async getPartnersForChallenge(challengeId: string): Promise<ApiResponse<Array<{ userId: string; displayName: string; avatarUrl?: string; startedAt: string }>>> {
    await delay(300)
    void challengeId
    // Mock: return empty array (real impl would query users with seekingPartners=true for this challenge)
    return success([])
  },

  /**
   * Toggle whether the current user is seeking partners for a challenge
   */
  async toggleSeekingPartners(userChallengeId: string, seeking: boolean): Promise<ApiResponse<{ seekingPartners: boolean }>> {
    await delay(200)
    void userChallengeId
    return success({ seekingPartners: seeking })
  },
}
